import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { useState } from "react";

import {
  Box,
  InputAdornment,
  IconButton,
  alpha,
  TextField,
  FormLabel,
  FormControl,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { FormField, FormItem, FormMessage } from "@/app/global-components/form";

interface RegisterFormFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  placeholder: string;
  description?: string;
  inputType?: string;
  formControl: Control<T>;
}

const RegisterFieldView = <T extends FieldValues>({
  name,
  label,
  placeholder,
  inputType,
  formControl,
}: RegisterFormFieldProps<T>) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field }) => (
        <FormItem>
          <Box sx={{ mb: 2 }}>
            <FormLabel
              sx={{
                color: "text.primary",
                fontWeight: 600,
                fontSize: "0.875rem",
                mb: 1,
                display: "block",
              }}
            >
              {label}
            </FormLabel>
          </Box>

          <FormControl fullWidth>
            <TextField
              variant="outlined"
              placeholder={placeholder}
              type={
                inputType === "password" && showPassword
                  ? "text"
                  : inputType || "text"
              }
              fullWidth
              InputProps={{
                endAdornment:
                  inputType === "password" ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            bgcolor: alpha("#8B5CF6", 0.1),
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#8B5CF6",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#8B5CF6",
                    borderWidth: 2,
                  },
                },
              }}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default RegisterFieldView;
